<template>
  <v-dialog :model-value="value" width="500px" @click:outside="closeModal">
    <BaseDialogCard v-if="value" :title="tkey('modalTitle')" @close="closeModal">
      <v-container fluid class="pa-0 reset-product-data__container">
        <v-row class="mt-4 border-bottom">
          <p class="mb-4">
            {{ tkey('description') }}
          </p>
        </v-row>
        <!-- reset options list -->
        <div class="mt-4 pb-5">
          <v-row>
            <span class="font-weight-bold">
              {{ tkey('kpis') }}
            </span>
          </v-row>
          <!-- specific fields to reset -->
          <v-row v-for="attr in dataToReset" :key="attr" class="checkbox-selection-row">
            <v-checkbox
              :model-value="currentSelections[attr]"
              class="rtls-checkbox mx-2"
              @update:model-value="updateCurrentSelections(attr, $event)"
            />
            <span>{{ normalizeTranslation(`scenarioInputPage.attributes.${attr}`, $t) }}</span>
          </v-row>
        </div>
      </v-container>
      <template #footer>
        <PageActions
          :has-data-errors="hasDataErrors"
          :has-data-changes="true"
          :show-discard="false"
          save-btn-text="reset"
          @save="resetProducts"
        />
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { filter, forEach, get, omit, size } from 'lodash';
import { mapState } from 'pinia';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';

// stores
import { useContextStore } from '@/stores/context';

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },

    dataToResetEnum: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const { tkey, normalizeTranslation } = useInternationalization(
      'scenarioInputPage.resetProductDataModal'
    );

    const { dependentProductsEnabled, freshEnabled } = useFeature();

    return {
      tkey,
      normalizeTranslation,
      dependentProductsEnabled,
      freshEnabled,
    };
  },

  data() {
    return {
      dataToReset: [],
      currentSelections: {},
    };
  },

  computed: {
    ...mapState(useContextStore, ['getClientConfig']),

    hasDataErrors() {
      return !size(this.selectedDataToReset);
    },

    hasDefaultParentChildData() {
      return get(this.getClientConfig, 'features.setDefaultParentChildData', false);
    },

    hasProductsInResetEnabled() {
      return get(this.getClientConfig, 'features.productsInResetEnabled', false);
    },

    hasTransactionWeightEnabled() {
      return get(this.getClientConfig, 'features.transactionWeightEnabled', false);
    },

    hasKPKUEnabled() {
      return get(this.getClientConfig, 'features.kpkuEnabled', false);
    },

    spacebreaksFromDataEnabled() {
      return get(this.getClientConfig, 'features.spacebreaksFromDataEnabled', false);
    },

    selectedDataToReset() {
      return filter(Object.keys(this.currentSelections), attr => !!this.currentSelections[attr]);
    },

    hasPeriodIDColumnEnabled() {
      return get(
        this.getClientConfig,
        'features.productAttributeEditorPage.periodIDColumnEnabled',
        false
      );
    },
  },

  created() {
    this.dataToReset = this.getDefaultDataToReset();
    this.setDefaultCurrentSelections();
  },

  methods: {
    updateCurrentSelections(field, shouldReset) {
      this.currentSelections[field] = shouldReset;
      this.setTheoreticalValuesForReset(field, shouldReset);
    },

    // This returns list of product keys that can be reset to WP level values
    getDefaultDataToReset() {
      // don't include fields that can't be reset
      const keysToOmit = [
        'isNewProduct',
        'productKey',
        'productKeyDisplay',
        'itemDescription',
        'deleteNewProduct',
        'futureId',
        'fromTemplate',
        'palletContent',
        'packageTypeDescription',
        'productSeries',
        'inReset',
        'recentlySold',
        'assortment',
        'originalMargin',
        'originalPrice',
        // We omit theoretical values to avoid duplicated rows in the modal
        // as those get automatically selected when margin or price is selected for reset
        'theoreticalMargin',
        'theoreticalPrice',
        'transferPriceInRange',
        'originalTransferPrice',
        'purchasePriceInRange',
        'originalPurchasePrice',
        'maintainOption',
        'originalShrinkToSalesRatio',
      ];
      // if there is no parent-child data on WP level, also omit parentProduct
      if (!this.hasDefaultParentChildData) {
        keysToOmit.push('parentProduct');
      }

      // if transaction weight feature is not enabled, exclude transaction weight field
      if (!this.hasTransactionWeightEnabled) {
        keysToOmit.push('averageTransactionWeight');
      }

      // if spacebreaks from data feature is not enabled, omit sc eligibility
      if (!this.spacebreaksFromDataEnabled) {
        keysToOmit.push('storeClassEligibility');
      }

      if (!this.hasKPKUEnabled) {
        keysToOmit.push('kpku');
      }

      if (this.hasPeriodIDColumnEnabled) {
        keysToOmit.push('periodID');
      }

      if (!this.dependentProductsEnabled) {
        keysToOmit.push('dependentSource');
      }

      if (!this.freshEnabled) {
        keysToOmit.push('shrinkToSalesRatio');
      }

      // set default options to reset, omit unnecessary keys from enum
      const enumSubset = omit(this.dataToResetEnum, keysToOmit);
      return Object.values(enumSubset);
    },

    setDefaultCurrentSelections() {
      forEach(this.dataToReset, attr => {
        this.currentSelections[attr] = true;
        this.setTheoreticalValuesForReset(attr, true);
      });
    },

    resetProducts() {
      this.$emit('reset-product-data', this.selectedDataToReset);
      this.closeModal();
    },

    closeModal() {
      this.$emit('close');
    },

    setTheoreticalValuesForReset(field, shouldReset) {
      // if margin or price is selected for reset, also select their
      // theoretical values and prevent duplicated rows in the modal
      if (field === 'margin') {
        this.currentSelections.theoreticalMargin = shouldReset;
      }

      if (field === 'price') {
        this.currentSelections.theoreticalPrice = shouldReset;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.reset-product-data {
  &__container {
    font-size: 1.2rem;
    max-height: 500px;
  }
}

.border-bottom {
  border-bottom: $assortment-border-colour 1px solid;
}

:deep(.dialog-card) {
  .dialog-card__footer {
    justify-content: flex-end;
  }
}
</style>
